import React, { useState } from 'react'
import { css } from '@emotion/css'
import { CsDeleteIcon } from '../../../../legacy-design-system/icons'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { theme as deprecatedTheme } from '../../../../styled'
import { WithTooltip } from '../../../../design-system'
import {
  FormattedMessage,
  FormattedDate,
  FormattedRelativeTime
} from 'react-intl'
import { selectRelativeUnits } from '../../../react-intl-helpers'
import { ConfirmationDialog } from '../../../../legacy-design-system/modal'
import { AttackEventNoteHistoryItemProps } from './attack-event-discussion'
import { MutationDeleteAttackEventNote } from './actions/delete-attack-event-note-mutation'

const cssHeader = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${deprecatedTheme.space[1]}px;
  background: ${theme.color.neutral20};
  border-bottom: 1px solid ${theme.color.neutral30};
  color: ${theme.color.neutral60};
`

const cssHeaderLeftContent = css`
  display: flex;
  align-items: center;
  width: 100%;
  font-family: ${deprecatedTheme.fontFamilies.primaryCondensed};
`

const cssHeaderUserName = css`
  font-weight: ${deprecatedTheme.fontWeights.primary.bold};
  margin-right: 4px;
`

const cssHeaderCreationTime = css`
  margin-right: 4px;
  cursor: text;
`

const cssHeaderEditedStatus = css`
  font-weight: ${deprecatedTheme.fontWeights.primary.bold};
  color: ${theme.color.red50};
  cursor: text;
`

const cssHeaderAvatar = css`
  height: 26px;
  width: 26px;
  border-radius: 3px;
  margin-right: ${deprecatedTheme.space[1]}px;
`

const cssHeaderDeleteIcon = css`
  width: 14px;
  height: 20px;
  cursor: pointer;
  color: ${theme.color.neutral100};
`

const dateTimeFormat: Intl.DateTimeFormatOptions = {
  month: '2-digit',
  day: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit'
}

export function AttackEventNoteHistoryItemHeader(
  props: AttackEventNoteHistoryItemProps
) {
  const { attackEventNote } = props
  const { createdByUserName, createdByUserAvatarUrl } = attackEventNote
  return (
    <div className={cssHeader}>
      <div className={cssHeaderLeftContent}>
        <img
          data-testid="attack-event-discussion-note-header-avatar-img"
          className={cssHeaderAvatar}
          src={
            createdByUserAvatarUrl
              ? createdByUserAvatarUrl
              : 'images/onboarding/default.png'
          }
        />
        <span className={cssHeaderUserName}>{createdByUserName}</span>
        <HistoryItemHeaderCommentTime {...props} />
        <HistoryItemHeaderEditStatus {...props} />
      </div>
      <HistoryItemHeaderDeleteButton {...props} />
    </div>
  )
}

function HistoryItemHeaderEditStatus({
  attackEventNote
}: AttackEventNoteHistoryItemProps) {
  const renderTooltipCallback = () => {
    return (
      <span>
        {attackEventNote.lastUpdatedByUserName} {'- '}
        <FormattedDate
          value={attackEventNote.lastModifiedTime}
          {...dateTimeFormat}
        />
      </span>
    )
  }

  //  TODO: i18n translation
  const renderChildren = () => {
    return (
      <span className={cssHeaderEditedStatus}>
        {'- '}
        <FormattedMessage id="detail_fieldEdited" />
      </span>
    )
  }
  // eslint-disable-next-line unicorn/prefer-ternary
  if (attackEventNote.creationTime < attackEventNote.lastModifiedTime) {
    return (
      <WithTooltip
        placement="top"
        renderTooltipContent={renderTooltipCallback}
        children={renderChildren}
      />
    )
  } else {
    return null
  }
}

function HistoryItemHeaderCommentTime({
  attackEventNote
}: AttackEventNoteHistoryItemProps) {
  const renderTooltipCallback = () => {
    return (
      <FormattedDate value={attackEventNote.creationTime} {...dateTimeFormat} />
    )
  }

  const renderChildren = () => {
    const { value, unit } = selectRelativeUnits(attackEventNote.creationTime)
    return (
      <span className={cssHeaderCreationTime}>
        <FormattedMessage id="title_userCommented" />{' '}
        <FormattedRelativeTime value={value} unit={unit} numeric="auto" />
      </span>
    )
  }

  return (
    <WithTooltip
      placement="top"
      renderTooltipContent={renderTooltipCallback}
      children={renderChildren}
    />
  )
}

function HistoryItemHeaderDeleteButton(props: AttackEventNoteHistoryItemProps) {
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const renderDeleteTooltip = () => (
    <FormattedMessage id="action_deleteComment" />
  )

  const renderDeleteButton = () => (
    <CsDeleteIcon
      className={cssHeaderDeleteIcon}
      onClick={() => setDeleteDialogOpen(true)}
    />
  )

  return (
    <React.Fragment>
      <WithTooltip
        placement="top"
        renderTooltipContent={renderDeleteTooltip}
        children={renderDeleteButton}
      />
      <HistoryItemHeaderDeleteConfirmation
        open={isDeleteDialogOpen}
        setOpen={setDeleteDialogOpen}
        {...props}
      />
    </React.Fragment>
  )
}

function HistoryItemHeaderDeleteConfirmation({
  open,
  setOpen,
  organizationId,
  attackEventUuid,
  attackEventNote
}: {
  readonly open: boolean
  readonly setOpen: (boolean) => void
} & AttackEventNoteHistoryItemProps) {
  const deleteParams = {
    variables: {
      organizationId,
      attackEventUuid,
      attackEventNoteUuid: attackEventNote.uuid
    }
  }

  return (
    <MutationDeleteAttackEventNote>
      {(deleteAttackEventNote) => {
        return (
          <ConfirmationDialog
            size="small"
            isOpen={open}
            renderTitle={() => (
              <FormattedMessage id="title_pleaseConfirmAction" />
            )}
            renderBody={() => (
              <FormattedMessage id="detail_deleteNote_confirmation" />
            )}
            renderConfirmMessage={() => <FormattedMessage id="action_delete" />}
            renderCancelMessage={() => <FormattedMessage id="action_cancel" />}
            onCancel={() => setOpen(false)}
            onConfirm={() => {
              setOpen(false)
              // eslint-disable-next-line @typescript-eslint/no-floating-promises
              deleteAttackEventNote(deleteParams)
            }}
          />
        )
      }}
    </MutationDeleteAttackEventNote>
  )
}
